import { Link, NavLink } from "react-router-dom";
import { Show, UserButton, useAuth, useClerk, useUser } from "@clerk/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Menu, X, Crown, LogOut, MapPin, Share2, MessageSquare, Heart, UserRound, LayoutDashboard, Car, Users, Store } from "lucide-react";
import { InstallButton } from "./InstallApp";
import { useUserLocation, locLabel } from "../lib/location";
import { useSite } from "../lib/site";
import { useFavorites } from "../lib/favorites";
import { imgUrl } from "../lib/api";
import EditPencil from "./EditPencil";
import { NotificationBell } from "./HeaderIcons";
import ShareButton from "./ShareButton";
import PromoButtons from "./PromoButtons";
import { ThemeSegment } from "./ThemeToggle";
import BrandName from "./BrandName";

const LINKS = [
  { to: "/cars", label: "Browse Cars", id: "nav-browse", icon: Car },
  { to: "/cars?seller_type=private", label: "Private Owners", id: "nav-private", icon: Users },
  { to: "/dealers", label: "Dealer Stores", id: "nav-dealers", icon: Store },
];

export const Logo = ({ editable = true }) => {
  const { branding } = useSite();
  return (
    <div className="flex items-center gap-1 min-w-0 shrink-0">
      <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 min-w-0">
        {branding.logo_url
          ? <img src={imgUrl(branding.logo_url)} alt="" className="w-8 h-8 sm:w-9 sm:h-9 shrink-0 rounded-full object-cover" data-testid="brand-logo-img" />
          : <span className="w-8 h-8 sm:w-9 sm:h-9 shrink-0 rounded-full flex items-center justify-center" style={{ background: "var(--gold-grad)" }}><Crown className="w-4 h-4 text-black" strokeWidth={2.4} /></span>}
        <BrandName name={branding.brand_name} color={branding.brand_color} font={branding.brand_font} className="text-xl sm:text-2xl" testid="brand-name" />
      </Link>
      {editable && <EditPencil section="logo" testid="logo-edit" />}
    </div>
  );
};

const UserName = ({ className = "", testid = "header-user-name" }) => {
  const { user } = useUser();
  const { me } = useSite();
  const name = me?.name || user?.firstName;
  return name ? <span className={`text-sm text-gray-200 max-w-[110px] truncate ${className}`} data-testid={testid}>{name}</span> : null;
};

const LogoutButton = ({ testid, className = "" }) => {
  const { signOut } = useClerk();
  return (
    <button onClick={() => signOut({ redirectUrl: "/" })} className={`flex items-center gap-1.5 text-sm text-gray-300 hover:text-[#E5C158] transition-colors ${className}`} data-testid={testid}>
      <LogOut className="w-4 h-4" />Logout
    </button>
  );
};

const CityChip = ({ testid, className = "" }) => {
  const { location, setAskOpen } = useUserLocation();
  return (
    <button onClick={() => setAskOpen(true)} className={`flex items-center gap-1 text-xs text-gray-300 hover:text-[#E5C158] border border-white/10 hover:border-[#D4AF37]/50 rounded-full px-2.5 py-1 transition-colors max-w-[220px] ${className}`} data-testid={testid}>
      <MapPin className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" /><span className="truncate">{locLabel(location)}</span>
    </button>
  );
};

const MenuRow = ({ to, icon: Icon, children, testid, badge }) => (
  <NavLink to={to} end className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "text-[#F5E08F] bg-white/[0.03]" : "text-gray-200 hover:bg-white/[0.03] hover:text-[#E5C158]"}`} data-testid={testid}>
    <Icon className="w-4 h-4 text-[#D4AF37]" /><span className="flex-1">{children}</span>{badge > 0 && <span className="text-[10px] font-bold text-black rounded-full px-1.5 min-w-[18px] text-center" style={{ background: "var(--gold-grad)" }}>{badge}</span>}
  </NavLink>
);

function MenuPanel({ onClose }) {
  const { ids } = useFavorites();
  const { isSignedIn } = useAuth();
  const ref = useRef(null);
  useEffect(() => {
    // Close on outside tap, ignoring the toggle button and Clerk's portalled account popover.
    const h = (e) => {
      const t = e.target;
      if (ref.current?.contains(t) || t.closest?.('[data-testid="mobile-menu-toggle"], [class*="cl-"]')) return;
      onClose();
    };
    const k = (e) => e.key === "Escape" && onClose();
    document.addEventListener("pointerdown", h);
    document.addEventListener("keydown", k);
    return () => { document.removeEventListener("pointerdown", h); document.removeEventListener("keydown", k); };
  }, [onClose]);
  return (
    <>
      <div ref={ref} className="absolute z-50 right-0 lg:right-6 top-full w-full lg:w-80 lg:mt-2 border-t lg:border gold-line lg:rounded-2xl bg-[#0d0f14] shadow-2xl p-4 flex flex-col gap-1 max-h-[78vh] overflow-y-auto" onClick={(e) => e.target.closest("a") && onClose()} data-testid="nav-menu">
        <Show when="signed-in">
          <div className="flex items-center gap-3 px-3 pb-3 mb-1 border-b border-white/10" data-testid="menu-profile">
            <UserButton /><div className="min-w-0 flex-1"><UserName testid="m-menu-user-name" className="block font-semibold" /><Link to="/dashboard?tab=profile" className="text-xs text-[#E5C158] hover:underline" data-testid="m-nav-profile">Edit profile</Link></div>
          </div>
        </Show>
        <div className="lg:hidden flex flex-col gap-1">
          {LINKS.map((l) => <MenuRow key={l.id} to={l.to} icon={l.icon} testid={`m-${l.id}`}>{l.label}</MenuRow>)}
          <Show when="signed-in">
            <MenuRow to="/chat" icon={MessageSquare} testid="m-nav-chat">Chats & Offers</MenuRow>
            <MenuRow to="/dashboard" icon={LayoutDashboard} testid="m-nav-dashboard">Dashboard</MenuRow>
          </Show>
        </div>
        <MenuRow to={isSignedIn ? "/dashboard?tab=saved" : "/sign-in"} icon={Heart} testid="menu-saved" badge={ids.size}>Saved cars</MenuRow>
        <Show when="signed-in"><MenuRow to="/dashboard?tab=profile" icon={UserRound} testid="menu-profile-link">My profile</MenuRow></Show>
        <div className="px-3 pt-3 pb-1"><p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Theme</p><ThemeSegment /></div>
        <div className="grid grid-cols-2 gap-2 pt-3 mt-2 border-t border-white/10">
          <ShareButton className="btn-outline text-xs w-full !py-2" testid="m-share-app"><Share2 className="w-3.5 h-3.5" />Share</ShareButton>
          <InstallButton testid="m-install-btn" className="text-xs w-full" />
        </div>
        <Link to="/sell" className="btn-gold text-sm mt-2 lg:hidden" data-testid="m-sell-btn">Sell Your Car</Link>
        <Show when="signed-out"><Link to="/sign-in" className="btn-outline text-sm mt-2" data-testid="m-signin-btn">Sign In</Link></Show>
        <Show when="signed-in"><div className="flex justify-end pt-2"><LogoutButton testid="m-logout-btn" /></div></Show>
      </div>
    </>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const linkCls = ({ isActive }) => `text-sm transition-colors duration-200 whitespace-nowrap ${isActive ? "text-[#F5E08F]" : "text-gray-300 hover:text-[#E5C158]"}`;
  const menuBtn = <button className="w-9 h-9 rounded-full border border-white/10 hover:border-[#D4AF37]/60 flex items-center justify-center text-[#E5C158] transition-colors" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open} data-testid="mobile-menu-toggle">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>;
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/80 border-b gold-line" data-testid="site-header">
      <div className="relative max-w-7xl 2xl:max-w-[1440px] mx-auto px-3 sm:px-6 lg:px-8 h-14 lg:h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 shrink-0"><Logo /></div>
        <nav className="hidden lg:flex items-center gap-4 xl:gap-6 min-w-0">
          {LINKS.map((l) => <NavLink key={l.id} to={l.to} className={linkCls} data-testid={l.id} end>{l.label}</NavLink>)}
          <Show when="signed-in"><NavLink to="/chat" className={linkCls} data-testid="nav-chat">Chats</NavLink><NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">Dashboard</NavLink></Show>
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <CityChip testid="header-city-chip" className="hidden lg:flex max-w-[150px]" />
          <span className="hidden lg:flex"><NotificationBell /></span>
          <span className="lg:hidden flex"><NotificationBell testid="m-header-bell-btn" /></span>
          <Link to="/sell" className="btn-gold text-xs lg:text-sm !py-1.5 lg:!py-2 !px-3 lg:!px-5" data-testid="header-sell-btn">Sell</Link>
          <Show when="signed-out"><span className="hidden lg:inline-flex"><Link to="/sign-in" className="btn-outline text-sm !py-2" data-testid="header-signin-btn">Sign In</Link></span></Show>
          {menuBtn}
        </div>
        {open && <MenuPanel onClose={close} />}
      </div>
      <div className="lg:hidden px-3 pb-2 -mt-0.5 flex items-center justify-between gap-2" data-testid="m-header-location-row"><CityChip testid="m-header-city-chip" className="max-w-[110px]" /><PromoButtons prefix="m-" btnClass="!px-2 !text-[10px]" /></div>
      <div className="hidden lg:flex justify-end items-center gap-4 px-8 pb-2 -mt-1 max-w-7xl mx-auto"><Show when="signed-in"><span className="text-xs text-gray-400 flex items-center gap-1">Welcome, <UserName className="!text-[#E5C158] font-semibold" testid="header-welcome-name" /></span></Show><PromoButtons prefix="header-" /></div>
    </header>
  );
}
